import { ChangeDetectionStrategy, Component, computed, contentChildren, effect, forwardRef, input, type OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { mergeClasses } from 'src/app/@core/utils/merge-classes';
import { segmentedItemVariants, segmentedVariants, type ZardSegmentedVariants } from './segmented.variants';
import { IconComponent } from '../icon/icon.component';
import { TooltipModule } from 'primeng/tooltip';

import type { ClassValue } from 'clsx';

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: string; // PrimeNG icon class (e.g., 'pi pi-check'), lucide kebab-case, or raw SVG string
  iconSource?: 'lucide' | 'svg' | 'primeng';
  iconClass?: string; // extra CSS classes for the icon (e.g., 'text-violet-500')
  tooltip?: string; // tooltip shown on hover (useful for icon-only options)
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'; // defaults to 'top'
  disabled?: boolean;
}
@Component({
  selector: 'z-segmented-item',
  standalone: true,
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
})
export class ZardSegmentedItemComponent {
  readonly value = input.required<string>();
  readonly label = input.required<string>();
  readonly icon = input<string>(); // PrimeNG icon class (e.g., 'pi pi-check')
  readonly disabled = input(false);
}

@Component({
  selector: 'z-segmented',
  exportAs: 'zSegmented',
  standalone: true,
  imports: [IconComponent, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .ai-light-gradient-base {
      background: #FDFDFD !important;
    }
    .ai-light-gradient {
      background: linear-gradient(119.87deg, #FFF6EF 2.03%, #EFF4FF 90.29%) !important;
    }
  `],
  template: `
    <div [class]="classes() + ' bg-gray-200'" role="tablist" [attr.aria-label]="zAriaLabel()">
      @if (zOptions().length > 0) {
        @for (option of zOptions(); track option.value) {

          <button
            type="button"
            role="tab"
            [class]="getItemClasses(option.value)"
            [disabled]="option.disabled || zDisabled()"
            [attr.aria-selected]="isSelected(option.value)"
            [attr.aria-controls]="option.value + '-panel'"
            [attr.id]="option.value + '-tab'"
            [pTooltip]="option.tooltip || ''"
            [tooltipPosition]="option.tooltipPosition || 'top'"
            (click)="selectOption(option.value)"
          >
            @if (option.icon && option.iconSource) {
              <app-icon [type]="option.icon" [source]="option.iconSource" [size]="'sm'" [class]="option.iconClass || ''" class="shrink-0" />
              <span class="ml-1.5">{{ option.label }}</span>
            } @else if (option.icon) {
              <i [class]="option.icon" class="icon-sm"></i>
            } @else {
              {{ option.label }}
            }
          </button>
        }
      } @else {
        @for (item of items(); track item.value()) {
          <button
            type="button"
            role="tab"
            [class]="getItemClasses(item.value())"
            [disabled]="item.disabled() || zDisabled()"
            [attr.aria-selected]="isSelected(item.value())"
            [attr.aria-controls]="item.value() + '-panel'"
            [attr.id]="item.value() + '-tab'"
            (click)="selectOption(item.value())"
          >
            @if (item.icon()) {
              <i [class]="item.icon()"></i>
            } @else {
              {{ item.label() }}
            }
          </button>
        }
      }
    </div>
  `,
  host: {
    '[class]': 'wrapperClasses()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZardSegmentedComponent),
      multi: true,
    },
  ],
})
export class ZardSegmentedComponent implements ControlValueAccessor, OnInit {
  private readonly itemComponents = contentChildren(ZardSegmentedItemComponent);

  readonly class = input<ClassValue>('');
  readonly zSize = input<ZardSegmentedVariants['zSize']>('default');
  readonly zVariant = input<ZardSegmentedVariants['zVariant']>('default');
  readonly zSeverity = input<ZardSegmentedVariants['zSeverity']>('default');
  readonly zOptions = input<SegmentedOption[]>([]);
  readonly zDefaultValue = input<string>('');
  readonly zDisabled = input(false);
  readonly zAriaLabel = input<string>('Segmented control');

  readonly zChange = output<string>();

  protected readonly selectedValue = signal<string>('');
  protected readonly items = signal<readonly ZardSegmentedItemComponent[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => { };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = () => { };

  constructor() {
    effect(() => {
      this.items.set(this.itemComponents());
    });
  }

  ngOnInit() {
    // Initialize with default value
    if (this.zDefaultValue()) {
      this.selectedValue.set(this.zDefaultValue());
    }
  }

  protected readonly classes = computed(() => mergeClasses(segmentedVariants({ zSize: this.zSize(), zVariant: this.zVariant(), zSeverity: this.zSeverity() }), this.class()));

  protected readonly wrapperClasses = computed(() => 'inline-block');

  protected getItemClasses(value: string): string {
    return segmentedItemVariants({
      zSize: this.zSize(),
      isActive: this.isSelected(value),
      zVariant: this.zVariant(),
      zSeverity: this.zSeverity(),
    });
  }

  protected isSelected(value: string): boolean {
    return this.selectedValue() === value;
  }

  protected selectOption(value: string) {
    if (this.zDisabled()) return;

    const option = this.zOptions().find(opt => opt.value === value);
    const item = this.items().find(item => item.value() === value);

    if (option?.disabled || item?.disabled()) return;

    this.selectedValue.set(value);
    this.onChange(value);
    this.onTouched();
    this.zChange.emit(value);
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.selectedValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // Handled by zDisabled input
  }
}
